import React from "react";
import { CreditResult } from "../types";
import AdviceBlock from "./AdviceBlock";
import { AlertTriangle, ShieldCheck } from "lucide-react";
import { roundTo } from "../utils/math_helpers";

interface Props {
  data: CreditResult;
  // «Подробнее у ассистента» в рекомендациях — без колбэка кнопка не показывается.
  onAskAssistant?: (question: string) => void;
}

export default function CreditTab({ data, onAskAssistant }: Props) {
  // Helper to resolve colors for risks
  const getRiskStyle = (risk: string) => {
    switch (risk) {
      case "Низкий риск":
        return {
          bg: "bg-emerald-50 border-emerald-100 text-emerald-800",
          badge: "bg-emerald-100 text-emerald-800",
          icon: <ShieldCheck className="w-6 h-6 text-emerald-500" />,
          desc: "Контрагент характеризуется высоким уровнем кредитной надежности. Вероятность задержки платежей или неисполнения обязательств минимальна."
        };
      case "Средний риск":
        return {
          bg: "bg-amber-50 border-amber-100 text-amber-800",
          badge: "bg-amber-100 text-amber-800",
          icon: <AlertTriangle className="w-6 h-6 text-amber-500" />,
          desc: "Умеренный кредитный риск. Имеются незначительные финансовые отклонения или повышенное долговое плечо. Рекомендуется мониторинг взаиморасчетов."
        };
      default:
        return {
          bg: "bg-rose-50 border-rose-100 text-rose-800",
          badge: "bg-rose-100 text-rose-800",
          icon: <AlertTriangle className="w-6 h-6 text-rose-500" />,
          desc: "Высокий уровень кредитного риска! Контрагент перегружен обязательствами или генерирует операционные убытки. Требуется предоплата или банковское обеспечение."
        };
    }
  };

  const risk = getRiskStyle(data.riskCategory);

  const getGroupBadge = (group: 'Low' | 'Medium' | 'High') => {
    switch (group) {
      case 'High':
        return <span className="bg-emerald-50 border border-emerald-250/30 text-emerald-800 t-meta font-mono font-extrabold px-2.5 py-0.5 rounded uppercase tracking-wider">Высокий (3)</span>;
      case 'Medium':
        return <span className="bg-amber-50 border border-amber-250/30 text-amber-805 t-meta font-mono font-extrabold px-2.5 py-0.5 rounded uppercase tracking-wider">Средний (2)</span>;
      default:
        return <span className="bg-rose-50 border border-rose-250/30 text-rose-800 t-meta font-mono font-extrabold px-2.5 py-0.5 rounded uppercase tracking-wider">Низкий (1)</span>;
    }
  };

  // Нет числового норматива у К1-К7 (только категория риска) — вместо width%-бара,
  // которому не из чего взять позицию, 3-сегментный индикатор: сколько сегментов
  // активно, определяет group (Low=1, Medium=2, High=3 — именно так закрашены
  // бейджи getGroupBadge выше).
  const riskSegments = (group: "Low" | "Medium" | "High") => {
    const lit = group === "High" ? 3 : group === "Medium" ? 2 : 1;
    const color = group === "High" ? "bg-emerald-500" : group === "Medium" ? "bg-amber-500" : "bg-rose-500";
    return (
      <div className="flex gap-1">
        {[0, 1, 2].map((i) => (
          <span key={i} className={`w-3.5 h-1.5 rounded-full ${i < lit ? color : "bg-slate-150"}`} />
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Risk Category Header Card */}
      <div className={`p-6 rounded-2xl border ${risk.bg} flex flex-col sm:flex-row items-start gap-4 shadow-sm transition-all duration-300 hover:shadow-md`}>
        <div className="p-3 bg-white rounded-xl shadow-inner border border-slate-100 shrink-0">
          {risk.icon}
        </div>
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <h3 className="font-extrabold text-sm md:text-base font-display text-slate-950 tracking-tight uppercase tracking-wider">Категория кредитного риска: {data.riskCategory}</h3>
          </div>
          <p className="text-xs md:text-sm text-slate-700 leading-relaxed font-semibold">{risk.desc}</p>
        </div>
      </div>

      {/* Credit Index F Progress Block */}
      <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm grid grid-cols-1 md:grid-cols-3 gap-6 items-center transition-all duration-300 hover:shadow-md">
        <div className="space-y-3.5 md:col-span-2">
          <div className="flex items-baseline gap-2 pb-1.5 border-b border-slate-100/75">
            <span className="text-[10px] font-mono text-slate-450 font-bold uppercase tracking-wider">Сводный кредитный индекс F</span>
            <span className="text-4xl font-black font-display text-indigo-700 tracking-tighter">{data.fScore}</span>
          </div>
          <p className="text-xs text-slate-500 leading-relaxed font-medium">
            Коэффициент <strong className="font-mono text-indigo-900 bg-indigo-50/50 px-1 py-0.5 rounded">F</strong> рассчитывается по весам распределения коэффициентов <strong className="font-mono text-slate-700 font-bold">(N1-N5)</strong> в пяти группах: К1 Автономия, К2 Мобильность, К3 Обеспеченность, К4 Покрытие, К5 Мгновенность, К6 Рентабельность, К7 Деловая активность.
          </p>
          
          {/* Progress bar representing F-score */}
          <div className="relative pt-1">
            <div className="overflow-hidden h-2.5 text-xs flex rounded-full bg-slate-100 border border-slate-200/40">
              <div 
                style={{ width: `${(data.fScore * 100).toFixed(0)}%` }} 
                className="shadow-inner flex flex-col text-center whitespace-nowrap text-white justify-center bg-indigo-650 transition-all duration-500"
              ></div>
            </div>
            <div className="flex flex-wrap gap-x-2 gap-y-0.5 justify-between t-micro font-mono font-bold text-slate-400 mt-1.5 uppercase tracking-wider">
              <span>Критический риск (0.00)</span>
              <span>Порог устойчивости (0.50)</span>
              <span>Идеальный профиль (0.925)</span>
            </div>
          </div>
        </div>

        {/* Breakdown of N distribution weights */}
        <div className="p-4 bg-slate-50 border border-slate-100 rounded-xl space-y-2.5 shadow-xs">
          <span className="text-[10px] font-mono font-extrabold text-slate-400 block border-b border-slate-200/55 pb-1 uppercase tracking-wider">Распределение весов N</span>
          <div className="space-y-1.5 text-xs font-mono">
            <div className="flex justify-between items-center bg-white p-1 rounded px-2 shadow-xs border border-slate-100/50">
              <span className="text-slate-500">N1 (Крайне низкий):</span>
              <span className="font-bold text-rose-600 font-mono">{(data.nDistribution.n1 * 100).toFixed(1)}%</span>
            </div>
            <div className="flex justify-between items-center bg-white p-1 rounded px-2 shadow-xs border border-slate-100/50">
              <span className="text-slate-500">N2 (Низкий):</span>
              <span className="font-bold text-orange-500 font-mono">{(data.nDistribution.n2 * 100).toFixed(1)}%</span>
            </div>
            <div className="flex justify-between items-center bg-white p-1 rounded px-2 shadow-xs border border-slate-100/50">
              <span className="text-slate-500">N3 (Удовлетворит.):</span>
              <span className="font-bold text-amber-600 font-mono">{(data.nDistribution.n3 * 100).toFixed(1)}%</span>
            </div>
            <div className="flex justify-between items-center bg-white p-1 rounded px-2 shadow-xs border border-slate-100/50">
              <span className="text-slate-500">N4 (Высокий):</span>
              <span className="font-bold text-teal-600 font-mono">{(data.nDistribution.n4 * 100).toFixed(1)}%</span>
            </div>
            <div className="flex justify-between items-center bg-white p-1 rounded px-2 shadow-xs border border-slate-100/50">
              <span className="text-slate-500">N5 (Превосходный):</span>
              <span className="font-bold text-emerald-600 font-mono">{(data.nDistribution.n5 * 100).toFixed(1)}%</span>
            </div>
          </div>
        </div>
      </div>

      {/* Corporate K1..K7 Indicators Cards Grid */}
      <div className="space-y-4">
        <h4 className="font-extrabold font-display text-slate-900 border-b border-slate-100 pb-2 text-sm md:text-base">
          Индивидуальный анализ коэффициентов кредитоспособности (К1-К7)
        </h4>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Card K1 */}
          <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm flex flex-col justify-between hover:shadow-md transition-all duration-300 hover:-translate-y-1 hover:border-indigo-150 group">
            <div className="space-y-3">
              <div className="flex justify-between items-start">
                <span className="t-meta font-mono font-bold bg-indigo-50 border border-indigo-100 text-indigo-700 px-2 py-0.5 rounded">К1: Автономия</span>
                {getGroupBadge(data.details.K1.group)}
              </div>
              <p className="font-extrabold text-xs md:text-sm text-slate-950 font-display min-h-[40px] leading-tight text-slate-900 group-hover:text-indigo-900 transition-colors">{data.details.K1.label}</p>
              <p className="text-xs text-slate-500 leading-snug font-medium">Рассчитывает независимость баланса заемщика от обязательств перед третьими лицами.</p>
            </div>
            <div className="mt-4 pt-3 border-t border-slate-50 flex justify-between items-center font-mono">
              <span className="text-[10px] uppercase font-bold text-slate-400">Значение:</span>
              <div className="flex items-baseline gap-2">
              {riskSegments(data.details.K1.group)}
              <span className="text-base font-black text-slate-900">{data.details.K1.value} <span className="text-[10px] text-slate-400 uppercase tracking-widest font-sans font-bold">коэф.</span></span>
              </div>
            </div>
          </div>

          {/* Card K2 */}
          <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm flex flex-col justify-between hover:shadow-md transition-all duration-300 hover:-translate-y-1 hover:border-indigo-150 group">
            <div className="space-y-3">
              <div className="flex justify-between items-start">
                <span className="t-meta font-mono font-bold bg-indigo-50 border border-indigo-100 text-indigo-700 px-2 py-0.5 rounded">К2: Мобильность</span>
                {getGroupBadge(data.details.K2.group)}
              </div>
              <p className="font-extrabold text-xs md:text-sm text-slate-950 font-display min-h-[40px] leading-tight text-slate-900 group-hover:text-indigo-900 transition-colors">{data.details.K2.label}</p>
              <p className="text-xs text-slate-500 leading-snug font-medium">Демонстрирует удельный вес подвижных материальных активов во всеобщем имуществе.</p>
            </div>
            <div className="mt-4 pt-3 border-t border-slate-50 flex justify-between items-center font-mono">
              <span className="text-[10px] uppercase font-bold text-slate-400">Значение:</span>
              <div className="flex items-baseline gap-2">
              {riskSegments(data.details.K2.group)}
              <span className="text-base font-black text-slate-900">{data.details.K2.value} <span className="text-[10px] text-slate-400 uppercase tracking-widest font-sans font-bold">коэф.</span></span>
              </div>
            </div>
          </div>

          {/* Card K3 */}
          <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm flex flex-col justify-between hover:shadow-md transition-all duration-300 hover:-translate-y-1 hover:border-indigo-150 group">
            <div className="space-y-3">
              <div className="flex justify-between items-start">
                <span className="t-meta font-mono font-bold bg-indigo-50 border border-indigo-100 text-indigo-700 px-2 py-0.5 rounded">К3: Обеспеченность СОС</span>
                {getGroupBadge(data.details.K3.group)}
              </div>
              <p className="font-extrabold text-xs md:text-sm text-slate-950 font-display min-h-[40px] leading-tight text-slate-900 group-hover:text-indigo-900 transition-colors">{data.details.K3.label}</p>
              <p className="text-xs text-slate-500 leading-snug font-medium">Определяет долю оборотного потока средств, профинансированных собственными силами.</p>
            </div>
            <div className="mt-4 pt-3 border-t border-slate-50 flex justify-between items-center font-mono">
              <span className="text-[10px] uppercase font-bold text-slate-400">Значение:</span>
              <div className="flex items-baseline gap-2">
              {riskSegments(data.details.K3.group)}
              <span className="text-base font-black text-slate-900">{data.details.K3.value} <span className="text-[10px] text-slate-400 uppercase tracking-widest font-sans font-bold">коэф.</span></span>
              </div>
            </div>
          </div>

          {/* Card K4 */}
          <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm flex flex-col justify-between hover:shadow-md transition-all duration-300 hover:-translate-y-1 hover:border-indigo-150 group">
            <div className="space-y-3">
              <div className="flex justify-between items-start">
                <span className="t-meta font-mono font-bold bg-indigo-50 border border-indigo-100 text-indigo-700 px-2 py-0.5 rounded">К4: Покрытие</span>
                {getGroupBadge(data.details.K4.group)}
              </div>
              <p className="font-extrabold text-xs md:text-sm text-slate-950 font-display min-h-[40px] leading-tight text-slate-900 group-hover:text-indigo-900 transition-colors">{data.details.K4.label}</p>
              <p className="text-xs text-slate-500 leading-snug font-medium">Параметр покрытия краткосрочного долга всеми оборотными запасами.</p>
            </div>
            <div className="mt-4 pt-3 border-t border-slate-50 flex justify-between items-center font-mono">
              <span className="text-[10px] uppercase font-bold text-slate-400">Значение:</span>
              <div className="flex items-baseline gap-2">
              {riskSegments(data.details.K4.group)}
              <span className="text-base font-black text-slate-900">{data.details.K4.value} <span className="text-[10px] text-slate-400 uppercase tracking-widest font-sans font-bold">коэф.</span></span>
              </div>
            </div>
          </div>

          {/* Card K5 */}
          <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm flex flex-col justify-between hover:shadow-md transition-all duration-300 hover:-translate-y-1 hover:border-indigo-150 group">
            <div className="space-y-3">
              <div className="flex justify-between items-start">
                <span className="t-meta font-mono font-bold bg-indigo-50 border border-indigo-100 text-indigo-700 px-2 py-0.5 rounded">К5: Мгновенность</span>
                {getGroupBadge(data.details.K5.group)}
              </div>
              <p className="font-extrabold text-xs md:text-sm text-slate-950 font-display min-h-[40px] leading-tight text-slate-900 group-hover:text-indigo-900 transition-colors">{data.details.K5.label}</p>
              <p className="text-xs text-slate-500 leading-snug font-medium">Доля срочной задолженности, которая может быть ликвидирована немедленно наличным капиталом.</p>
            </div>
            <div className="mt-4 pt-3 border-t border-slate-50 flex justify-between items-center font-mono">
              <span className="text-[10px] uppercase font-bold text-slate-400">Значение:</span>
              <div className="flex items-baseline gap-2">
              {riskSegments(data.details.K5.group)}
              <span className="text-base font-black text-slate-900">{data.details.K5.value} <span className="text-[10px] text-slate-400 uppercase tracking-widest font-sans font-bold">коэф.</span></span>
              </div>
            </div>
          </div>

          {/* Card K6 */}
          <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm flex flex-col justify-between hover:shadow-md transition-all duration-300 hover:-translate-y-1 hover:border-indigo-150 group">
            <div className="space-y-3">
              <div className="flex justify-between items-start">
                <span className="t-meta font-mono font-bold bg-indigo-50 border border-indigo-100 text-indigo-700 px-2 py-0.5 rounded">К6: Рентабельность</span>
                {getGroupBadge(data.details.K6.group)}
              </div>
              <p className="font-extrabold text-xs md:text-sm text-slate-950 font-display min-h-[40px] leading-tight text-slate-900 group-hover:text-indigo-900 transition-colors">{data.details.K6.label}</p>
              <p className="text-xs text-slate-500 leading-snug font-medium">Прибыльность использования капитала (на основе совокупного баланса активов - ROA).</p>
            </div>
            <div className="mt-4 pt-3 border-t border-slate-50 flex justify-between items-center font-mono">
              <span className="text-[10px] uppercase font-bold text-slate-400">Значение:</span>
              <div className="flex items-baseline gap-2">
              {riskSegments(data.details.K6.group)}
              <span className="text-base font-black text-slate-900">{data.details.K6.value} <span className="text-[10px] text-slate-400 uppercase tracking-widest font-sans font-bold">коэф.</span></span>
              </div>
            </div>
          </div>

          {/* Card K7 */}
          <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm flex flex-col justify-between hover:shadow-md transition-all duration-300 hover:-translate-y-1 hover:border-indigo-150 group md:col-span-2 lg:col-span-1">
            <div className="space-y-3">
              <div className="flex justify-between items-start">
                <span className="t-meta font-mono font-bold bg-indigo-50 border border-indigo-100 text-indigo-700 px-2 py-0.5 rounded">К7: Оборачиваемость</span>
                {getGroupBadge(data.details.K7.group)}
              </div>
              <p className="font-extrabold text-xs md:text-sm text-slate-950 font-display min-h-[40px] leading-tight text-slate-900 group-hover:text-indigo-900 transition-colors">{data.details.K7.label}</p>
              <p className="text-xs text-slate-500 leading-snug font-medium">Коэффициент деловой активности - скорость обращения совокупного баланса активов.</p>
            </div>
            <div className="mt-4 pt-3 border-t border-slate-50 flex justify-between items-center font-mono">
              <span className="text-[10px] uppercase font-bold text-slate-400">Значение:</span>
              <div className="flex items-baseline gap-2">
              {riskSegments(data.details.K7.group)}
              <span className="text-base font-black text-slate-900">{data.details.K7.value} <span className="text-[10px] text-slate-400 uppercase tracking-widest font-sans font-bold">об.</span></span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <AdviceBlock conclusion={data.conclusion} recommendations={data.recommendations} onAskAssistant={onAskAssistant} />
    </div>
  );
}
